import { useSyncExternalStore } from "react";

/**
 * Tiny module-level store for the in-call portal target.
 *
 * The Shell-level `<LiveKitRoom>` provides the LiveKit React context that
 * `<InCallView>` needs (audio/video tracks, participants, data channel).
 * But InCallView must render INSIDE the suite-chrome's content slot so it
 * picks up the shell's flex chain (sidebar offset, header offset). If it
 * rendered as a sibling of `<SuiteAppShell>` it would fall outside the
 * layout and stack below the shell — visible only by scrolling.
 *
 * Solution: `RoomRoute` (which renders inside the shell content slot)
 * mounts an empty target div and publishes its ref through this store.
 * `CallRouteAwareRenderer` (which lives inside the Shell-level
 * LiveKitRoom) reads the target and `createPortal`s `<InCallView />` into
 * it. Context follows the React tree (not the DOM), so the portaled
 * InCallView still sees LiveKit context from its React-parent
 * LiveKitRoom even though its DOM parent is RoomRoute's div.
 */
let target: HTMLElement | null = null;
const listeners = new Set<() => void>();

export function setInCallPortalTarget(next: HTMLElement | null): void {
  if (target === next) return;
  target = next;
  listeners.forEach((l) => l());
}

export function useInCallPortalTarget(): HTMLElement | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => target,
    () => null,
  );
}
